/* eslint-disable react/display-name */
import CheckIcon from '@mui/icons-material/Check'
import {
	type BaseSelectProps,
	FormControl,
	FormHelperText,
	InputLabel,
	Select,
} from '@mui/material'
import {
	forwardRef,
	type ReactNode,
	useCallback,
	useImperativeHandle,
	useRef,
} from 'react'
import { useTranslation } from 'react-i18next'

import * as S from './styles'

type IProps = Omit<BaseSelectProps, 'multiple' | 'value'> & {
	handleChange: BaseSelectProps['onChange']
	label?: string | ReactNode
	options: Record<string, string>
	disabled?: boolean
	required?: boolean
	fullWidth?: boolean
	fieldError?: string
	helperText?: string
} & (
		| { multiple: true; value: readonly string[] }
		| { multiple?: false; value?: string }
	)

const Dropdown = forwardRef<HTMLDivElement, IProps>((props, ref) => {
	const { t } = useTranslation()
	const {
		options,
		value,
		handleChange,
		label,
		disabled,
		multiple,
		required,
		fullWidth,
		fieldError,
		helperText,
		...rest
	} = props

	const selectRef = useRef<HTMLDivElement>(null)
	useImperativeHandle(
		ref,
		() => ({
			...(selectRef.current as HTMLDivElement),
			focus: () => {
				selectRef.current?.focus()
				selectRef.current?.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
				})
			},
		}),
		[]
	)
	const handleRenderMultipleValues = useCallback(
		(selected: unknown): ReactNode =>
			(selected as readonly string[]).map(value => options?.[value]).join(', '),
		[options]
	)
	return (
		<FormControl
			error={!!fieldError}
			variant={rest.variant || 'filled'}
			required={required}
			style={{ width: fullWidth ? '100%' : 'auto' }}
		>
			<InputLabel>{label}</InputLabel>
			<Select
				renderValue={multiple ? handleRenderMultipleValues : undefined}
				ref={selectRef}
				{...rest}
				value={value ?? ''}
				onChange={handleChange}
				disabled={disabled}
				multiple={multiple}
			>
				{options &&
					Object.keys(options).map(option => (
						<S.MenuItem
							key={option}
							value={option}
							style={{ whiteSpace: 'break-spaces' }}
							$multiple={multiple}
						>
							{options[option]}
							{multiple && value.includes(option) && (
								<CheckIcon color="success" fontSize="inherit" />
							)}
						</S.MenuItem>
					))}
			</Select>
			{(fieldError === 'required' || helperText) && (
				<FormHelperText style={{ marginLeft: '-5px', marginBottom: '-20px' }}>
					{fieldError === 'required' ? t('formField.required') : helperText}
				</FormHelperText>
			)}
		</FormControl>
	)
})

export { Dropdown }
